import PropTypes from "prop-types";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatRoleLabel } from "@/lib/teamErrors";
import { TEAM_INVITES_UI_ENABLED } from "@/lib/featureFlags";

const MANAGEABLE_ROLES = ["owner", "admin", "member", "viewer"];
const INVITE_ROLES = [
  { value: "admin", label: "Admin" },
  { value: "member", label: "Member" },
];

export function TeamSettingsDialogs({ team }) {
  return (
    <>
      {TEAM_INVITES_UI_ENABLED ? (
        <Dialog open={team.inviteOpen} onOpenChange={team.setInviteOpen}>
          <DialogContent className="w-[calc(100vw-1.5rem)] max-w-[420px] max-h-[90dvh] overflow-y-auto" data-testid="invite-team-modal">
            <DialogHeader><DialogTitle>Invite teammate</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input
                placeholder="Work email"
                value={team.inviteEmail}
                onChange={(e) => team.setInviteEmail(e.target.value)}
                className="h-11 sm:h-9"
                data-testid="invite-email"
              />
              <Select value={team.inviteRole} onValueChange={team.setInviteRole}>
                <SelectTrigger className="h-11 sm:h-9 text-[12px]" data-testid="invite-role">
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent>
                  {INVITE_ROLES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="rounded-md border border-slate-200 p-2 text-[12px] text-slate-600">
                Invites apply to <span className="font-medium text-slate-900">{team.workspaceLabel}</span>.
              </div>
              <Button className="w-full" onClick={team.sendInvite} disabled={team.inviteBusy} data-testid="invite-send">
                {team.inviteBusy ? "Sending…" : "Send invite"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      ) : null}

      <Dialog open={!!team.roleDialog} onOpenChange={(open) => { if (!open) team.setRoleDialog(null); }}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-[400px] max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Change role</DialogTitle>
          </DialogHeader>
          {team.roleDialog ? (
            <div className="space-y-3">
              <p className="text-[12.5px] text-slate-600">
                Update role for <strong>{team.roleDialog.display_name || team.roleDialog.email}</strong>.
              </p>
              <Select value={team.roleValue} onValueChange={team.setRoleValue}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MANAGEABLE_ROLES.map((r) => (
                    <SelectItem key={r} value={r}>{formatRoleLabel(r)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button className="w-full" onClick={team.saveRole} disabled={team.roleBusy}>
                {team.roleBusy ? "Saving…" : "Save role"}
              </Button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!team.removeTarget} onOpenChange={(open) => { if (!open) team.setRemoveTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove team member?</AlertDialogTitle>
            <AlertDialogDescription>
              {team.removeTarget
                ? `${team.removeTarget.display_name || team.removeTarget.email} will lose access immediately and their sessions will be revoked.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={team.removeBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 hover:bg-rose-700"
              disabled={team.removeBusy}
              onClick={(e) => { e.preventDefault(); team.confirmRemove(); }}
            >
              {team.removeBusy ? "Removing…" : "Remove member"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

TeamSettingsDialogs.propTypes = {
  team: PropTypes.object.isRequired,
};
